import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./SuperAdminRoomControl.css";

const API_BASE = import.meta.env.VITE_API_URL || "/api";

const SuperAdminRoomControl = () => {
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busySlot, setBusySlot] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  // Protect route
  useEffect(() => {
    if (localStorage.getItem("superAdminAuth") !== "true") {
      navigate("/superadmin/login", { replace: true });
    }
  }, [navigate]);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchRooms = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/rooms`);
      const data = await res.json();
      if (data.success) setRooms(data.data || []);
    } catch (err) {
      console.error("Failed to fetch rooms:", err);
      showToast("Failed to load theaters", "error");
    }
  }, []);

  const fetchBookings = useCallback(async (date) => {
    try {
      const res = await fetch(`${API_BASE}/bookings/admin/by-date?date=${date}`);
      const data = await res.json();
      if (data.success) setBookings(data.data || []);
    } catch (err) {
      console.error("Failed to fetch bookings:", err);
      setBookings([]);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchRooms(), fetchBookings(selectedDate)])
      .finally(() => setLoading(false));
  }, [fetchRooms, fetchBookings, selectedDate]);

  // Find booking matching room + slot on selectedDate
  const findBooking = (roomId, slot) => {
    return bookings.find(
      (b) =>
        b.roomId === roomId &&
        b.slot === slot &&
        (b.state === "COMPLETED" ||
          (b.state === "PENDING" &&
            (Date.now() - new Date(b.createdAt).getTime()) < 10 * 60 * 1000))
    );
  };

  const getSlotStatus = (roomId, slot) => {
    const b = findBooking(roomId, slot);
    if (!b) return { status: "available", booking: null };
    if (b.meta?.adminBlocked) return { status: "admin-blocked", booking: b };
    if (b.state === "PENDING") return { status: "pending", booking: b };
    return { status: "customer-booked", booking: b };
  };

  const handleBlockSlot = async (room, slot) => {
    setBusySlot(`${room._id}-${slot}`);
    try {
      const res = await fetch(`${API_BASE}/bookings/admin/block-slot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          roomId: room._id,
          theater: room.title,
          slot,
          date: selectedDate,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Failed to block slot");
      showToast(`🔒 Blocked: ${room.title} @ ${slot}`, "success");
      await fetchBookings(selectedDate);
    } catch (err) {
      showToast(`❌ ${err.message}`, "error");
    } finally {
      setBusySlot(null);
    }
  };

  const handleUnblockSlot = async (room, slot, booking) => {
    setBusySlot(`${room._id}-${slot}`);
    try {
      const res = await fetch(`${API_BASE}/bookings/admin/unblock-slot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          merchantOrderId: booking.merchantOrderId,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Failed to unblock slot");
      showToast(`✓ Unblocked: ${room.title} @ ${slot}`, "success");
      await fetchBookings(selectedDate);
    } catch (err) {
      showToast(`❌ ${err.message}`, "error");
    } finally {
      setBusySlot(null);
    }
  };

  const handleToggle = (room, slot) => {
    const { status, booking } = getSlotStatus(room._id, slot);

    if (status === "customer-booked") {
      setConfirmModal({
        title: "⚠️ Customer Booking",
        message: `This slot is booked by a real customer (${booking?.name || "Unknown"}, ${booking?.whatsapp || "no phone"}). Unblocking it could cause double-booking. Are you ABSOLUTELY sure?`,
        confirmText: "Yes, Cancel Customer Booking",
        danger: true,
        action: () => {
          handleUnblockSlot(room, slot, booking);
          setConfirmModal(null);
        },
      });
      return;
    }

    if (status === "available") {
      handleBlockSlot(room, slot);
    } else {
      handleUnblockSlot(room, slot, booking);
    }
  };

  const formatPretty = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="src-page">
      {/* Header */}
      <header className="src-header">
        <button className="src-back-btn" onClick={() => navigate("/superadmin/dashboard")}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Dashboard
        </button>
        <div className="src-header-title">
          <h1>🎭 RoomCard Slot Control</h1>
          <p>Enable or disable specific time slots for any theater on any date</p>
        </div>
      </header>

      {/* Date picker */}
      <div className="src-date-bar">
        <div className="src-date-bar-inner">
          <div className="src-date-info">
            <span className="src-date-label">📅 Viewing slots for</span>
            <span className="src-date-value">{formatPretty(selectedDate)}</span>
          </div>
          <input
            type="date"
            className="src-date-input"
            value={selectedDate}
            min={today}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>
      </div>

      {/* Legend */}
      <div className="src-legend">
        <div className="src-legend-item">
          <span className="src-chip src-chip-available">Available</span>
          <span className="src-legend-desc">Click to block</span>
        </div>
        <div className="src-legend-item">
          <span className="src-chip src-chip-admin-blocked">Admin Blocked</span>
          <span className="src-legend-desc">Click to unblock</span>
        </div>
        <div className="src-legend-item">
          <span className="src-chip src-chip-customer">Customer Paid</span>
          <span className="src-legend-desc">Confirmed booking</span>
        </div>
        <div className="src-legend-item">
          <span className="src-chip src-chip-pending">Pending Payment</span>
          <span className="src-legend-desc">In progress</span>
        </div>
      </div>

      {/* Rooms */}
      <main className="src-rooms">
        {loading ? (
          <div className="src-loading">
            <div className="src-spinner" />
            <p>Loading theaters...</p>
          </div>
        ) : rooms.length === 0 ? (
          <div className="src-empty">
            <h3>No theaters found</h3>
            <p>Add theaters from the main admin panel first.</p>
          </div>
        ) : (
          rooms.map((room, idx) => (
            <div className="src-room-card" key={room._id} style={{ animationDelay: `${idx * 0.08}s` }}>
              <div className="src-room-header">
                <img
                  src={room.images?.[0] || "https://via.placeholder.com/120?text=No+Img"}
                  alt={room.title}
                  className="src-room-thumb"
                />
                <div className="src-room-info">
                  <h3>{room.title}</h3>
                  <p>📍 {room.location || "—"}</p>
                  <div className="src-room-meta">
                    <span>👥 Max {room.maxPeople}</span>
                    <span>₹{room.price?.toLocaleString()}</span>
                    <span>{room.timeSlots?.length || 0} slots</span>
                  </div>
                </div>
              </div>

              <div className="src-slots-section">
                <h4 className="src-slots-title">⏰ Time Slots</h4>
                {(!room.timeSlots || room.timeSlots.length === 0) ? (
                  <p className="src-no-slots">No time slots configured for this theater</p>
                ) : (
                  <div className="src-slots-grid">
                    {room.timeSlots.map((slot, i) => {
                      const { status, booking } = getSlotStatus(room._id, slot);
                      const slotKey = `${room._id}-${slot}`;
                      const isBusy = busySlot === slotKey;

                      return (
                        <button
                          key={i}
                          className={`src-slot src-slot-${status} ${isBusy ? "src-slot-busy" : ""}`}
                          onClick={() => !isBusy && handleToggle(room, slot)}
                          disabled={isBusy}
                          title={
                            status === "customer-booked"
                              ? `Booked by ${booking?.name || "customer"}`
                              : status === "pending"
                              ? "Awaiting payment"
                              : status === "admin-blocked"
                              ? "Manually blocked by admin"
                              : "Click to block"
                          }
                        >
                          <span className="src-slot-time">{slot}</span>
                          <span className="src-slot-state">
                            {isBusy
                              ? "..."
                              : status === "available"
                              ? "Available"
                              : status === "admin-blocked"
                              ? "🔒 Blocked"
                              : status === "pending"
                              ? "⏳ Pending"
                              : "✓ Booked"}
                          </span>
                          {booking?.name && (
                            <span className="src-slot-customer">{booking.name}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </main>

      {/* Toast */}
      {toast && (
        <div className={`src-toast src-toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Confirm modal */}
      {confirmModal && (
        <div className="src-modal-backdrop" onClick={() => setConfirmModal(null)}>
          <div className="src-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{confirmModal.title}</h3>
            <p>{confirmModal.message}</p>
            <div className="src-modal-actions">
              <button className="src-modal-cancel" onClick={() => setConfirmModal(null)}>
                Cancel
              </button>
              <button
                className={`src-modal-confirm ${confirmModal.danger ? "danger" : ""}`}
                onClick={confirmModal.action}
              >
                {confirmModal.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminRoomControl;